import { useQuery } from '@apollo/client';
import { DonorStatus, UserRole } from 'gql/types';
import { useMemo } from 'react';

import { GET_APP_SETTINGS_QUERY } from '../../settings/graphql/queries';

export interface IRoleRestriction {
  lockToRegion: boolean;
  lockToOffice: boolean;
  hideDashboard: boolean;
  hideRollingDates: boolean;
  hideCalendarDates: boolean;
  showLocationRecruitStats: boolean;
}

export interface IRecruiterDashboardSettings {
  excludedDates: string[]; // MM-dd format (holidays)
  workingDays: number[]; // ISO weekday [1-7], default [1,2,3,4,5]
  dayPrbcGoal: number; // pRBC goal per working day
  statusBreakpoints: [number, number];
  statusLabels: {
    low: string;
    mid: string;
    high: string;
  };
  roleRestrictionsEnabled: boolean;
  roleRestrictions: Partial<Record<UserRole, IRoleRestriction>>;
  activeDonorStatuses: DonorStatus[]; // Statuses considered "active" for pipeline health
  pendingDonorStatuses: DonorStatus[]; // Statuses considered "pending" for pipeline health
  approvedDonorStatuses: DonorStatus[]; // Statuses considered "approved" for pipeline health
  repeatDonationsThreshold: number; // Min donations to be considered a repeat donor
  churnRateBreakpoints: [number, number]; // [good max, ok max] — below first = Good, between = OK, above second = Poor
  churnRateLabels: {
    low: string; // Good (lowest churn)
    mid: string; // OK
    high: string; // Poor (highest churn)
  };
  pendingDaysBreakpoints: [number, number]; // [onTrack max, approaching max] — below first = On Track, between = Approaching, above = Overdue
  pendingDaysLabels: {
    low: string; // On Track (fewest days)
    mid: string; // Approaching
    high: string; // Overdue (most days)
  };
  donorDonationFrequency: number; // Expected days between donations (default: 56)
  donorShowRateBreakpoints: [number, number]; // [poor max, ok max] — like goal tracker: below first = Poor, between = OK, above = Good
  donorShowRateLabels: {
    low: string; // Poor (lowest show rate)
    mid: string; // OK
    high: string; // Good (highest show rate)
  };
  lapsedDonorBreakpoints: number[]; // Day markers for lapsed donor ranges (default: [30, 60, 90])
  lapsedDonorLabels: string[]; // Labels per range (length = breakpoints.length + 1)
  retiredDonorBreakpoints: number[]; // Day markers for retired donor (Unresponsive) ranges (default: [30, 60, 90])
  retiredDonorLabels: string[]; // Labels per range (length = breakpoints.length + 1)
  historyDataCutoff: string; // ISO date (YYYY-MM-DD) — periods before this are excluded from history charts (donor_status_change_history start)
  activeLocationThresholdDays: number; // Location is "active" if ≥1 donation within this many days (default: 90)
  avatars: Record<string, string>; // userId → base64 data URI
  defaultAvatarBase64: string | null; // Default avatar base64 data URI
  simulateRole: UserRole | null; // Admin-only: simulate viewing as this role
  simulateUserId: string | null; // Admin-only: simulate as this specific user
  donorEligibilityDays: number; // Days after registration before a new donor is eligible to donate (default: 40)
  sglThresholdMl: number; // mL threshold for SGL/DBL classification (default: 420)
  ffpSglProductIds: number[]; // WooCommerce product IDs for FFP SGL (multiplier = 1)
  ffpDblProductIds: number[]; // WooCommerce product IDs for FFP DBL (multiplier = 2)
  showDebug: boolean; // Admin-only: show Data Audit tab
}

const DEFAULT_SETTINGS: IRecruiterDashboardSettings = {
  excludedDates: [],
  workingDays: [1, 2, 3, 4, 5],
  dayPrbcGoal: 25,
  statusBreakpoints: [50, 75],
  statusLabels: {
    low: 'Offtrack',
    mid: 'At Risk',
    high: 'On Track',
  },
  roleRestrictionsEnabled: false,
  roleRestrictions: {},
  activeDonorStatuses: [
    DonorStatus.Active,
    DonorStatus.Approved,
    DonorStatus.AnnualLabworkDue,
  ],
  pendingDonorStatuses: [DonorStatus.Pending],
  approvedDonorStatuses: [DonorStatus.Approved],
  repeatDonationsThreshold: 4,
  churnRateBreakpoints: [5, 10],
  churnRateLabels: {
    low: 'Good',
    mid: 'OK',
    high: 'Poor',
  },
  pendingDaysBreakpoints: [30, 45],
  pendingDaysLabels: {
    low: 'On Track',
    mid: 'Approaching',
    high: 'Overdue',
  },
  donorDonationFrequency: 56,
  donorShowRateBreakpoints: [50, 75],
  donorShowRateLabels: {
    low: 'Poor',
    mid: 'OK',
    high: 'Good',
  },
  lapsedDonorBreakpoints: [30, 60, 90],
  lapsedDonorLabels: ['Recent', 'Moderate', 'Extended', 'Critical'],
  retiredDonorBreakpoints: [30, 60, 90],
  retiredDonorLabels: ['Recent', 'Moderate', 'Extended', 'Critical'],
  historyDataCutoff: '2025-08-01',
  activeLocationThresholdDays: 90,
  avatars: {},
  defaultAvatarBase64: null,
  simulateRole: null,
  simulateUserId: null,
  donorEligibilityDays: 40,
  sglThresholdMl: 420,
  ffpSglProductIds: [502, 8179],
  ffpDblProductIds: [503, 504],
  showDebug: false,
};

const SETTINGS_TAB = 'recruiter-dashboard';

export function useRecruiterDashboardSettings() {
  const { data, loading, error } = useQuery(GET_APP_SETTINGS_QUERY, {
    variables: { input: { settingsTab: SETTINGS_TAB } },
  });

  const settings: IRecruiterDashboardSettings = useMemo(
    () =>
      data?.getAppSettings?.settingsJson
        ? {
            ...DEFAULT_SETTINGS,
            ...data.getAppSettings.settingsJson,
          }
        : DEFAULT_SETTINGS,
    [data]
  );

  return { settings, loading, error };
}
